import { EmptyState } from "@/components/common/EmptyState";
import { ScreenHeader } from "@/components/common/ScreenHeader";
import { ThemedText } from "@/components/themed-text";
import { Loader } from "@/components/ui/loader";
import { BrandColors, Colors } from "@/constants/theme";
import { useColorScheme } from "@/hooks/use-color-scheme";
import {
  useInfiniteNotifications,
  useMarkNotificationRead,
} from "@/hooks/queries/use-notifications-query";
import {
  navigateFromNotification,
  parseNotificationData,
} from "@/lib/notification-navigation";
import { markAllAsRead, markAsRead } from "@/lib/services/notifications";
import { Notification } from "@/lib/types/notification";
import Ionicons from "@react-native-vector-icons/ionicons";
import { useRouter, useSegments } from "expo-router";
import React, { useCallback, useMemo, useRef } from "react";
import {
  FlatList,
  RefreshControl,
  StyleSheet,
  TouchableOpacity,
  View,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";

export default function NotificationsScreen() {
  const colorScheme = useColorScheme();
  const theme = Colors[colorScheme ?? "light"];
  const router = useRouter();
  const segments = useSegments();
  const handledKeys = useRef(new Set<string>());
  const { markOne, markAll } = useMarkNotificationRead();

  const {
    data,
    isLoading,
    isRefetching,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteNotifications();

  const notifications = useMemo(
    () => data?.pages.flatMap((page) => page.data.data) ?? [],
    [data],
  );

  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsRead();
      markAll();
    } catch (error) {
      console.error("Error marking all as read:", error);
    }
  };

  const handleNotificationPress = useCallback(
    async (item: Notification) => {
      const parsedData =
        typeof item.data === "string" ? JSON.parse(item.data) : item.data;
      const notificationData = parseNotificationData(parsedData);

      if (notificationData) {
        navigateFromNotification(notificationData, {
          router,
          segments,
          handledKeys: handledKeys.current,
          navigate: true,
        });
      }

      if (!item.read_at) {
        try {
          await markAsRead(item.id);
          markOne(item.id);
        } catch (error) {
          console.error("Error marking as read:", error);
        }
      }
    },
    [markOne, router, segments],
  );

  const getIcon = (type: string) => {
    switch (type) {
      case "success":
        return { name: "checkmark-circle", color: BrandColors.success };
      case "warning":
        return { name: "warning", color: BrandColors.warning };
      case "error":
        return { name: "alert-circle", color: BrandColors.error };
      default:
        return { name: "notifications", color: BrandColors.primary };
    }
  };

  const formatTime = (dateString: string) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";

    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) return "Just now";
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400)
      return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800)
      return `${Math.floor(diffInSeconds / 86400)}d ago`;

    return date.toLocaleDateString();
  };

  const renderItem = useCallback(
    ({ item }: { item: Notification }) => {
      const parsedData =
        typeof item.data === "string" ? JSON.parse(item.data) : item.data;

      if (!parsedData) return null;

      const icon = getIcon(parsedData.type || "info");
      const isRead = !!item.read_at;
      const title = parsedData.title || "Notification";
      const message = parsedData.message || "No message content";

      return (
        <TouchableOpacity
          style={styles.notificationItem}
          activeOpacity={0.7}
          onPress={() => handleNotificationPress(item)}
          accessibilityRole="button"
          accessibilityLabel={`${title}. ${message}. ${formatTime(item.created_at)}`}
          accessibilityState={{ selected: false }}
        >
          <View
            style={[
              styles.iconContainer,
              { backgroundColor: icon.color + "15" },
            ]}
          >
            <Ionicons
              name={icon.name as any}
              size={24}
              color={icon.color}
            />
          </View>
          <View style={styles.contentContainer}>
            <View style={styles.itemHeader}>
              <ThemedText
                style={[!isRead && styles.unreadText]}
                type="defaultSemiBold"
              >
                {title}
              </ThemedText>
              <ThemedText style={styles.itemTime} type="extraSmall">
                {formatTime(item.created_at)}
              </ThemedText>
            </View>
            <ThemedText numberOfLines={4} type="small">
              {message}
            </ThemedText>
          </View>
          {!isRead && <View style={styles.unreadBadge} />}
        </TouchableOpacity>
      );
    },
    [handleNotificationPress],
  );

  return (
    <SafeAreaView
      style={[styles.container, { backgroundColor: theme.background }]}
      edges={["top"]}
    >
      <ScreenHeader
        title="Notifications"
        rightComponent={
          <TouchableOpacity onPress={handleMarkAllAsRead}>
            <ThemedText style={styles.markReadText} type="extraSmall">
              Mark all
            </ThemedText>
          </TouchableOpacity>
        }
      />

      {isLoading ? (
        <Loader inline />
      ) : (
        <FlatList
          data={notifications}
          renderItem={renderItem}
          keyExtractor={(item) => item.id}
          contentContainerStyle={styles.listContent}
          ItemSeparatorComponent={() => (
            <View style={[styles.separator, { backgroundColor: theme.border }]} />
          )}
          refreshControl={
            <RefreshControl refreshing={isRefetching} onRefresh={refetch} />
          }
          onEndReached={() => {
            if (hasNextPage && !isFetchingNextPage) {
              fetchNextPage();
            }
          }}
          onEndReachedThreshold={0.5}
          ListFooterComponent={
            isFetchingNextPage ? (
              <Loader inline style={styles.footerLoader} />
            ) : null
          }
          ListEmptyComponent={
            <EmptyState
              icon="notifications-off-outline"
              title="No notifications yet"
              subtitle="Updates about your bookings will appear here."
            />
          }
        />
      )}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  markReadText: {
    fontSize: 12,
    color: BrandColors.primary,
    fontWeight: "600",
  },
  listContent: {
    paddingBottom: 24,
    flexGrow: 1,
  },
  footerLoader: {
    paddingVertical: 16,
  },
  notificationItem: {
    flexDirection: "row",
    padding: 16,
    alignItems: "center",
  },
  iconContainer: {
    width: 48,
    height: 48,
    borderRadius: 24,
    justifyContent: "center",
    alignItems: "center",
    marginRight: 16,
  },
  contentContainer: {
    flex: 1,
  },
  itemHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 4,
  },
  unreadText: {
    fontWeight: "700",
  },
  itemTime: {
    fontSize: 12,
    marginLeft: 8,
  },
  unreadBadge: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: BrandColors.primary,
    marginLeft: 8,
  },
  separator: {
    height: 1,
    backgroundColor: BrandColors.mediumGray + "50",
  },
});
